import { useCallback, useEffect, useMemo, useState } from 'react';
import { MappingType } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/mapping.pb';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { getFullName } from '@frontend/name';
import { FormRow, useDebouncedValue, useFormField } from '@frontend/design-system';
import {
  CustomMultiSelectInfiniteDropdown,
  CustomMultiSelectInfiniteDropdownOptionType,
} from '../CustomMultiSelectInfiniteDropdown';

interface Props {
  locationId: string;
  appointmentTypeId?: string;
}

export const usePractitionerCheckListDropdown = ({ locationId, appointmentTypeId }: Props) => {
  const { t } = useTranslation('schedule');
  const [selectedItems, setSelectedItems] = useState<CustomMultiSelectInfiniteDropdownOptionType[]>([]);
  const searchFieldProps = useFormField({ type: 'text' });
  const searchText = useDebouncedValue(searchFieldProps.value);
  const practitionerInfiniteListQuery = SchedulerV3.Helpers.useGetPractitionerInfiniteListForV3({
    locationId,
    searchText: searchText.length > 1 ? searchText : '',
  });
  const [hasError, setHasError] = useState(false);

  const listMappingsQuery = SchedulerV3.Queries.useListMappingsQuery({
    request: {
      locationIds: [locationId],
      appointmentTypeId: appointmentTypeId ?? '',
      mappingType: MappingType.PROVIDER_APPOINTMENT_TYPE,
      showInactive: true,
    },
    options: {
      enabled: !!appointmentTypeId && !!locationId,
    },
  });

  // @TODO: Need to remove the logic to find archive status based on provider list response once the list mapping API is updated.
  // Currently, list mapping query does not return archive status in `providerMetaData` object.
  const archivedProviderIds =
    practitionerInfiniteListQuery.data?.pages
      .flatMap((page) => page.rows)
      .filter((provider) => provider.isArchived)
      .map((provider) => provider.id) ?? [];

  const initialSelectedItems = useMemo(
    () =>
      (listMappingsQuery.data?.mappings ?? [])
        .reduce<CustomMultiSelectInfiniteDropdownOptionType[]>((acc, item) => {
          if (item.providerMetaData?.id) {
            acc.push({
              label: item.providerMetaData.name,
              value: item.providerMetaData.id,
              archived: archivedProviderIds.includes(item.providerMetaData.id),
            });
          }
          return acc;
        }, [])
        .sort((a, b) => a.label.localeCompare(b.label)),
    [listMappingsQuery.data?.mappings]
  );

  useEffect(() => {
    setSelectedItems(initialSelectedItems);
  }, [initialSelectedItems]);

  // hide dropdown if no practitioners loaded and no search text
  const shouldHideDropdown = !searchText && practitionerInfiniteListQuery.data?.pages[0].rows.length === 0;
  return {
    hasPractitionerCheckListDropdownError: hasError,
    resetSelection: useCallback(() => setSelectedItems([]), []),
    getPractitionerSelectedUnselectedValues: useCallback(() => {
      const selectedValues = selectedItems.map((item) => item.value);
      const initialValues = initialSelectedItems.map((item) => item.value);

      const newSelectedProviderIds = selectedValues.filter((value) => !initialValues.includes(value));
      const unSelectedProviderIds = initialValues.filter((value) => !selectedValues.includes(value));
      const mappingIdsForDelete =
        listMappingsQuery.data?.mappings
          ?.filter((mapping) => unSelectedProviderIds.includes(mapping.providerMetaData?.id ?? ''))
          .map((mapping) => mapping.id) ?? [];

      return {
        newSelectedProviderIds,
        mappingIdsForDelete,
      };
    }, [selectedItems]),
    PractitionerCheckListDropdownElement: !shouldHideDropdown && (
      <FormRow>
        <CustomMultiSelectInfiniteDropdown
          label={t('Assigned Providers')}
          placeholder={t('Select providers')}
          searchFieldProps={searchFieldProps}
          infiniteQueryResult={practitionerInfiniteListQuery}
          getOptionInfo={(provider) => ({
            archived: provider.isArchived,
            label: provider.displayName || getFullName(provider),
            value: provider.id,
          })}
          setHasError={setHasError}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          isLoading={listMappingsQuery.isFetching}
        />
      </FormRow>
    ),
  };
};
